// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';
import ContentContainer from '@/components/common/ContentContainer';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import ComponentSharedTextEditor from '@/components/strapi/Shared/ComponentSharedTextEditor';
import { BILD_TEXT_DESIGN_DEFAULTS_TYPED } from '@/components/common/defaults';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksBildTextBlock as BildTextBlockType } from '@/types/graphql';

type ComponentBlocksBildTextBlockProps = StrapiComponentProps<Omit<BildTextBlockType, 'bildTextVariant'>>;

const BildTextDesignBlock = ({
	host,
	bildTextCta,
	bildTextCopy = BILD_TEXT_DESIGN_DEFAULTS_TYPED.copy,
	bildTextImage,
	bildTextTitle = BILD_TEXT_DESIGN_DEFAULTS_TYPED.headline,
	bildTextContent,
	bildTextEyebrow = BILD_TEXT_DESIGN_DEFAULTS_TYPED.eyebrow,
	bildTextImagePosition,
	bildTextContentVariant
}: ComponentBlocksBildTextBlockProps) => {
	const theme = useTheme();
	const themeMode = theme.palette.mode;

	const editor = bildTextContent;
	const withCta = bildTextCta !== undefined && bildTextCta !== null;
	const ctaLink = bildTextCta?.hrefExternal ? bildTextCta?.hrefExternal : buildPathFromRelationLink(bildTextCta?.href);

	const textColor = theme.palette.common.white;
	const isImageRight = bildTextImagePosition.toLocaleLowerCase() === 'right';

	const renderCopy = () => {
		if (bildTextContentVariant?.toLocaleLowerCase() === 'normal') {
			return (
				<Typography variant="body-md" sx={{ color: textColor, whiteSpace: 'pre-line' }}>
					{bildTextCopy}
				</Typography>
			);
		}

		return (
			<ComponentSharedTextEditor
				blockContent={editor?.blockContent || bildTextCopy}
				variantBody={editor?.variantBody}
				variantHeading1={editor?.variantHeading1}
				variantHeading2={editor?.variantHeading2}
				variantHeading3={editor?.variantHeading3}
				variantHeading4={editor?.variantHeading4}
				variantHeading5={editor?.variantHeading5}
				variantHeading6={editor?.variantHeading6}
				overrides={{
					paragraph: {
						mt: 0,
						color: textColor,
						fontFamily: '"DIN Next LT Pro"',
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
						fontWeight: 400,
						lineHeight: 'clamp(1.375rem, 1.2857rem + 0.4464vw, 1.625rem)',
						letterSpacing: '-0.05px',
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					heading: {
						fontFamily: '"DIN Next LT Pro"',
						color: textColor,
						mb: 1
					},
					list: {
						color: textColor,
						pl: 4,
						'& li': {
							p: 0.3,
							fontFamily: '"DIN Next LT Pro"',
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					listItem: {
						fontFamily: '"DIN Next LT Pro"',
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
						color: textColor,
						'& .MuiTypography-root': {
							color: textColor,
							fontSize: 'inherit'
						},
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					link: {
						color: textColor,
						textDecoration: 'underline',
						fontFamily: '"DIN Next LT Pro"',
						'&:hover': {
							opacity: 0.8
						},
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					},
					quote: {
						color: textColor,
						borderColor: theme.palette.primary.light,
						backgroundColor: 'rgba(255, 255, 255, 0.1)',
						[theme.breakpoints.up('md')]: {
							fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
						}
					}
				}}
			/>
		);
	};

	const ContentStack = (
		<Stack>
			<Typography variant="label" sx={{ color: textColor, mb: { xs: 3, lg: 4 } }}>
				{bildTextEyebrow}
			</Typography>
			<Typography variant="title-md" sx={{ color: textColor, mb: { xs: 4, lg: 8 } }}>
				{bildTextTitle}
			</Typography>

			{renderCopy()}

			{withCta && (
				<Box sx={{ mt: { xs: 6, lg: 8 } }}>
					<UnderlineHoverCta
						href={ctaLink}
						label={bildTextCta?.label || BILD_TEXT_DESIGN_DEFAULTS_TYPED.ctaLabel}
						color={textColor}
						typographyVariant="body-md"
						fontWeight={500}
					/>
				</Box>
			)}
		</Stack>
	);

	return (
		<FadeInView>
			<ContentContainer disableGutters>
				{/* ── Mobile View ───────────────────────────────────── */}
				<Box
					sx={(t) => ({
						[`@media (min-width: ${t.layout.twoColMinWidthPx}px)`]: {
							display: 'none'
						}
					})}
				>
					{/* Image logic remains same */}
					<Box
						sx={{
							position: 'relative',
							width: 'auto',
							mx: theme.layout.pagePx,
							height: { xs: 260, sm: 320 },
							zIndex: 1,
							borderRadius: 2,
							overflow: 'hidden'
						}}
					>
						<StrapiImageRenderer
							host={host}
							imageData={bildTextImage}
							themeMode={themeMode}
							alt={bildTextImage?.alternativeText || 'Hero Image'}
							fill
							sizes="100vw"
						/>
					</Box>
					<Box
						sx={{
							position: 'relative',
							zIndex: 0,
							mt: -30,
							backgroundColor: theme.palette.grey[600],
							borderRadius: 2,
							pt: 8 + 30,
							pb: 8,
							px: theme.layout.pagePx
						}}
					>
						{ContentStack}
					</Box>
				</Box>

				{/* ── Desktop View ──────────────────────────────────── */}
				<Box
					sx={(t) => ({
						display: 'none',
						[`@media (min-width: ${t.layout.twoColMinWidthPx}px)`]: {
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							position: 'relative',
							minHeight: 'clamp(520px, 45vw, 640px)',
							columnGap: t.layout.desktopColumnGap
						}
					})}
				>
					<Box
						sx={{
							position: 'absolute',
							[isImageRight ? 'left' : 'right']: 'calc((100% - 100vw) / 2)',
							[isImageRight ? 'right' : 'left']: 'calc(100% - 1102px)',
							top: 0,
							bottom: 0,
							borderRadius: isImageRight ? '0 8px 8px 0' : '8px 0 0 8px',
							backgroundColor: theme.palette.grey[600],
							zIndex: 0
						}}
					/>

					{/* Order logic for Image Left/Right */}
					<Box
						sx={(t) => ({
							position: 'relative',
							zIndex: 1,
							flex: '1 1 0',
							minWidth: 0,
							maxWidth: '720px',
							order: isImageRight ? 1 : 2,
							pl: isImageRight ? t.layout.pagePx : 0,
							pr: isImageRight ? 0 : t.layout.pagePx,
							py: 10
						})}
					>
						{ContentStack}
					</Box>

					<Box
						sx={(t) => ({
							position: 'relative',
							zIndex: 1,
							flexShrink: 0,
							order: isImageRight ? 2 : 1,
							ml: isImageRight ? 0 : t.layout.desktopGutter,
							mr: isImageRight ? t.layout.desktopGutter : 0,
							width: `clamp(300px, 41.1vw, ${t.layout.heroPanelContentMaxWidthPx}px)`,
							aspectRatio: `${t.layout.heroPanelContentMaxWidthPx} / 392`,
							borderRadius: 2,
							overflow: 'hidden'
						})}
					>
						<StrapiImageRenderer host={host} imageData={bildTextImage} themeMode={themeMode} fill sizes="41vw" />
					</Box>
				</Box>
			</ContentContainer>
		</FadeInView>
	);
};

export default BildTextDesignBlock;
